import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppSignUpApi } from "./AppSignUpApi";
import { AppIcons } from "./AppIcons";
import { ImportPlainRules } from "./ImportPlain";
import { AppImportView, ImportShown } from "./AppImportStatus";
import { AppUpdateEvery } from "./AppUpdateEvery";
import { AppButton } from "./AppButton";

// AppSignUp - the sign-up route, `#signup`.
//
// ==================================================================================================
// !! IT IS A ROUTE, NOT A SECOND PAGE, AND THAT IS THE HOUSE RULING
// ==================================================================================================
//
// `kbuild.json` says "One target, one bundle" and `AppMain`'s header says why: routing is the HASH,
// so the whole app is one static file with no server rewrites. A second `.html` would need a
// catch-all rewrite that neither the Azure static website ($web on `wacappf58706`) nor `file://`
// provides, so a path-routed page would always be broken somewhere. Sign-up is therefore a view
// like any other.
//
// ==================================================================================================
// !! ⛔ THE STEP IS DERIVED, NEVER REMEMBERED
// ==================================================================================================
//
// The obvious shape is a wizard that advances a counter. It is wrong here for a reason that shows up
// on the first reload: a remembered "step 3" survives a refresh that has thrown away what it was
// standing on, so the visitor is shown a step whose preconditions have gone. `StepFor` answers from
// what is actually KNOWN, so a reload lands exactly where the facts put it.
//
// ==================================================================================================
// !! ⭐ REWRITTEN FOR THE PHONE-AND-DROPBOX FLOW (owner rulings 2026-09-22 and 2026-09-23)
// ==================================================================================================
//
// It began "1. Google, 2. Your number, 3. Your history, 4. Connect". Both ends of that are gone:
// "I don't want anything running in the google cloud" removed the Google sign-in that step 1 WAS,
// and "storing on Google is out ... Each user will supply their own existing dropbox account"
// removed the Drive the history was going to.
//
// ⛔ THE ORDER IS NOT COSMETIC, AND EACH POSITION IS EARNED:
//   1. NUMBER    - a CLAIM, not an identity. Anyone can type any number (`ClaimGate`).
//   2. CONNECT   - the pairing code. **This is what turns the claim into an identity**, because
//                  possession of the handset is the only proof this product ever had. It comes
//                  before Dropbox so that the irreversible act happens while the person is present
//                  and holding the phone, and so that a provisional row cannot collect a stranger's
//                  Dropbox grant before the real owner replaces it.
//   3. DROPBOX   - storage, attached to a number that is now PROVED.
//   4. HISTORY   - last, because it needs somewhere to put the result, which is step 3.
//
// ==================================================================================================
// !! ⭐ IT IS A CARD, AND THE STYLING IS PART OF THE ARGUMENT (owner ruling 2026-09-23, "give the ui
// !! a major facelift - this looks like the absolute bare minimum")
// ==================================================================================================
//
// This is the ONLY page in the product a stranger sees before deciding whether to hand it their
// WhatsApp history, so "looks unfinished" is not a cosmetic complaint - it is the product failing
// to make its case. Four things changed shape, and each answers something the old markup could not:
//
//   * ⭐ THE TRAIL SHOWS PROGRESS, NOT POSITION. Four identical pills said which step was open and
//     never said how much was behind them; `Mark` now renders DONE (a tick) / NOW / NEXT with the
//     connector coloured up to where the visitor has actually got.
//   * ⭐ THE WAITING SCREEN MOVES. It is the one screen with nothing to press - a second ask cancels
//     the code on its way - so motion is the only evidence it can offer that anything is happening.
//   * ⭐ THE CODE IS TYPOGRAPHY, NOT A SENTENCE. Eight characters read off one screen and typed into
//     another under a one-minute deadline, with a copy button beside them.
//   * ⭐ A REFUSAL IS A DESIGNED NOTICE. It was orange body text, which reads as breakage; it is
//     validation, which is the product working, and `Notice` is what that looks like.
//
// ⚠ NOTHING ABOUT THE FLOW MOVED. `StepFor`, every element id `AppMain` binds, and every wire
// contract in `AppSignUpApi` are untouched: ids are the seam between what is rendered and what
// happens, and changing one side of a seam produces a button that does nothing.
//
// ⚠ THERE IS NO GOOGLE PICKER ANY MORE, AND THAT DELETED A WHOLE STEP FROM THE PHONE DANCE. Under
// `drive.file` the app could not see a hand-uploaded file, so the subscriber had to upload the
// backup to Drive FIRST and pick it through the Picker (an API key, an app id, and the
// `docs.google.com/*` website restriction whose omission fails as "API developer key is invalid").
// The browser can simply open the local file, so step 4 is now: turn on the key, back up, choose the
// file. Nothing goes to a cloud before this page has read it.

/** Where the visitor is, derived from what is known about them. */
export class SignUpState {
    /** The number they have CLAIMED, or "" before they have. ⚠ Claimed is not proved - see Linked. */
    public Number: string = "";
    /** True once the phone is linked. **This is the identity**, not the box above. */
    public Linked: boolean = false;
    /** The Dropbox account they connected, for showing back. "" before they have. */
    public Dropbox: string = "";
    /**
     * Where to send them to approve Dropbox, as the service built it.
     *
     * ⚠ IT COMES FROM THE SERVICE, NEVER FROM THIS PAGE. The app key is the service's to hold and
     * the scope list must have exactly one source (`DropboxAuth.Scopes`); a URL assembled here would
     * be a second place for the scopes to drift, and a scope missing from a browser-built URL fails
     * later as a refused upload in front of a subscriber.
     */
    public DropboxUrl: string = "";
    /**
     * ⭐ WAVE 3 (DORMANT): Microsoft's sign-in page for OneDrive, as `/onedrive` built it with this page's state - ""
     * whenever the service does not offer OneDrive (switch off, an older service) or this page is not on the
     * registered origin (`AppOneDriveRules.OriginOk`). "" = the step is exactly the Dropbox step it always was.
     */
    public OneDriveUrl: string = "";
    /** "onedrive" once the archive's storage is OneDrive (then `Dropbox` holds "your OneDrive"); else "". */
    public Storage: string = "";
    /** The file they chose off the phone, or "" before they have chosen one. */
    public PickedName: string = "";
    /**
     * "" when the chosen file is a `.crypt15` (the key is asked for next). Otherwise which plaintext
     * WhatsApp database the page found inside it (`ImportPlainRules.Iphone` / `Android`) - an iPhone's
     * database has no key, so the step shows an Import button instead of a key box (`AskPlain`).
     */
    public PickedKind: string = "";
    /** True once their folder holds an imported archive. */
    public Imported: boolean = false;
    /**
     * ⭐ WHAT THE SERVICE SAYS BECAME OF THE UPLOADED BACKUP (`AppImportStatus`), or null when nothing is being
     * shown - no upload this page can ask about, or a service with no `/import` route (the cards then say what
     * they said before). Set by `AppMain` from the import watch; the cards only render it (`AppImportView`).
     */
    public Upload: ImportShown | null = null;
    /**
     * True once they have said they have no Android key backup to bring (an iPhone, or no backup
     * at all) and chose to go on without one.
     *
     * ⭐ IT IS A FACT, LIKE `Imported`, NOT A POSITION. `StepFor` reads it exactly as it reads
     * `Imported` - "the history question has been answered" - so the wizard still derives where the
     * visitor is from what is known, and a later reload lands where the facts put it.
     *
     * ⚠ IT LIVES WHERE EVERY OTHER SIGN-UP FACT LIVES, ON THIS OBJECT, AND NO LONGER. `Number`,
     * `Linked` and the session secret all end with the tab (see `AppMain.Secret` for why), so
     * persisting this one fact alone would restore a "skipped" answer onto a sign-up that no longer
     * exists. The SERVICE holds the durable half: a subscriber who never imports is built from live
     * messages alone.
     *
     * ⛔ THE PAGE MUST SAY WHAT IT COSTS. History from before today will not appear; new messages
     * will. `AllDone` says exactly that for a skipped sign-up rather than promising an archive of
     * everything.
     */
    public SkippedImport: boolean = false;
    /**
     * ⭐ THE SERVICE KEEPS WHAT WHATSAPP HANDS OVER AT LINKING (2026-10-13; `/dropbox`'s `history: true`, set in
     * `AppMain.OnDropboxDone`). It only chooses the words a visitor with NO backup reads (`SkipOffer`, `Facts`):
     * false = the words as they always were. In memory, like the rest of the sign-up.
     * ⛔ NO PROMISE OF AMOUNTS OR TIMES either way: WhatsApp decides what it hands over, and it may be nothing.
     */
    public LinkHistory: boolean = false;
    /** A validation sentence for the step in hand. NOT an error - see `Note`. */
    public Why: string = "";
    /**
     * The sentence in `Why` that is a notice about the ACCOUNT rather than a refusal of what was typed -
     * today WhatsApp's passkey demand (`SignUpAnswer.Passkey`, a flag from the service). "" for none.
     *
     * ⚠ IT HOLDS THE SENTENCE, NOT A BOOLEAN, so it can never outlive it: `Note` shows the ink notice only
     * while `Why` is still exactly this sentence, and every later `Why` (a refusal, "") is styled on its own.
     */
    public InfoWhy: string = "";
    /** True while a request is in flight, so the button can say so and refuse a second press. */
    public Busy: boolean = false;
    /**
     * The pairing code to type, or "".
     *
     * ⛔ IT LIVES ONLY AS LONG AS THE STEP. It is a bearer credential that links a device to this
     * WhatsApp account, and it is deliberately NOT persisted anywhere - not in storage, not in the
     * URL, not in the folder this page rebuilds from. A reload loses it, which is correct: by then
     * it has expired anyway, and showing an expired code is worse than showing none.
     */
    public Code: string = "";
    /** True while an ask is out and no answer has arrived. */
    public Waiting: boolean = false;
    /** True once the wait has gone on longer than an answer should take. */
    public Slow: boolean = false;
    /**
     * How long the wait for a code has gone on, in whole seconds - written by the page's one-second
     * tick, so a repaint in the middle of a wait starts from the right number.
     */
    public WaitedSec: number = 0;
    /**
     * What is in the number box right now. ⚠ NOT `Number`, which is the FACT that the service took
     * the claim (`StepFor` reads it). This only exists so a repaint - the busy state, a refusal - puts
     * back what the visitor typed instead of emptying the box under them (seen in Chrome 2026-10-01).
     */
    public Typed: string = "";
    /**
     * The Dropbox code in the box while it is being checked, for the same reason. ⚠ Cleared with the
     * answer: a Dropbox code works once, so a refused one is not offered back.
     */
    public DbxTyped: string = "";
    /** True once the service has been asked for the Dropbox page and has answered - either way. */
    public DropboxAsked: boolean = false;
}

export class AppSignUp {
    public static readonly Number: string = "number";
    public static readonly Connect: string = "connect";
    public static readonly Dropbox: string = "dropbox";
    public static readonly Import: string = "import";
    public static readonly Done: string = "done";

    /**
     * Which step the visitor is actually on.
     *
     * ⚠ EACH ANSWER IS A FACT, NOT A POSITION. Read top to bottom: without a number there is
     * nothing to link; until it is linked nobody has proved it is theirs, so no storage may be
     * attached to it; without storage there is nowhere for a history to land. This is what makes a
     * reload safe and what makes the page honest after a half-finished attempt.
     */
    public static StepFor = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return AppSignUp.Number; }
        try {
            if (nz(State.Number).trim().length === 0) { return AppSignUp.Number; }
            if (State.Linked !== true) { return AppSignUp.Connect; }
            if (nz(State.Dropbox).trim().length === 0) { return AppSignUp.Dropbox; }
            // ⚠ THE HISTORY QUESTION HAS TWO HONEST ANSWERS: imported, or "I have no key backup to
            // bring" (iPhone, or none made). Either one moves them on; neither is a counter.
            if (State.Imported !== true && State.SkippedImport !== true) { return AppSignUp.Import; }
            return AppSignUp.Done;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.StepFor");
            return AppSignUp.Number;
        }
    };

    /**
     * The four steps' names, in order.
     *
     * ⚠ ONE SOURCE FOR THE TRAIL AND THE COUNTER. The trail draws them and the card's eyebrow names
     * the one in hand - and below 420px the trail's labels are hidden, so the eyebrow is the ONLY
     * place a phone reader learns which dot is which. Two lists would be two chances to disagree on
     * exactly that surface.
     */
    private static readonly Names: string[] =
        ["Your number", "Link WhatsApp", "Your Dropbox", "Your history"];

    /**
     * How far along, 1-based, with `Names.length + 1` meaning finished.
     *
     * ⚠ IT IS DERIVED FROM THE STEP, WHICH IS DERIVED FROM THE FACTS. Nothing here counts anything;
     * see this file's header for why a remembered position is wrong on the first reload.
     */
    private static Reached = (Step: string): number => {
        if (getCoreUtils().KErrorMode === true) { return 1; }
        try {
            if (Step === AppSignUp.Connect) { return 2; }
            if (Step === AppSignUp.Dropbox) { return 3; }
            if (Step === AppSignUp.Import) { return 4; }
            if (Step === AppSignUp.Done) { return AppSignUp.Names.length + 1; }
            return 1;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Reached");
            return 1;
        }
    };

    /** The whole route: one centred card, and the standing promise underneath it. */
    public static Html = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Step: string = AppSignUp.StepFor(State);
            const Out: string[] = [];
            Out.push("<section class=\"signup\"><div class=\"panel\">");
            Out.push(AppSignUp.Crown(Step));
            Out.push(AppSignUp.Trail(Step, State.SkippedImport === true));
            Out.push(AppSignUp.WithNote(AppSignUp.Body(Step, State), State));
            Out.push("</div>");
            Out.push(AppSignUp.Vouch());
            Out.push("</section>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Html");
            return "";
        }
    };

    /** The card's head: the mark, and where in the four steps this is - in words. */
    private static Crown = (Step: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const At: number = AppSignUp.Reached(Step);
            // ⚠ "SIGNED UP", NOT "ALL DONE". The card beneath says the archive is still being
            // built, and "all done" above "being built" is finished and unfinished at once.
            let Say: string = "Signed up";
            if (At <= AppSignUp.Names.length) {
                Say = "Step " + String(At) + " of " + String(AppSignUp.Names.length) + " &#183; "
                    + kHTMLCore.EscapeHtml(AppSignUp.Names[At - 1]);
            }
            // ⚠ NO LOGO AND NO WORDMARK HERE ANY MORE (2026-10-01). The top bar already says WACommander
            // in the brand face one line above the card; repeating both inside it spent the card's
            // first row on something the visitor had just read, and carried the one hex colour left in
            // this view. The crown is now only what the card adds: where in the four steps this is.
            return "<header class=\"crown\"><p class=\"eyebrow\">" + Say + "</p></header>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Crown");
            return "";
        }
    };

    /**
     * The four steps as DONE / NOW / NEXT - progress, not four equal pills.
     *
     * ⚠ ALL FOUR ARE NAMED FROM THE START, so nobody is surprised by the one that needs the phone.
     *
     * ⚠ A SKIPPED HISTORY IS NOT TICKED. A tick says "done", and an import that never happened is
     * not done - it is marked with a dash so the trail does not claim a history the archive lacks.
     */
    public static Trail = (Step: string, Skipped: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const At: number = AppSignUp.Reached(Step);
            const Out: string[] = ["<ol class=\"wiz\">"];
            let Ord: number = 1;
            for (const Word of AppSignUp.Names) {
                Out.push(AppSignUp.Mark(Ord, At, Word, Skipped && Ord === AppSignUp.Names.length));
                Ord = Ord + 1;
            }
            Out.push("</ol>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Trail");
            return "";
        }
    };

    /**
     * ⚠ NOT A LINK, AND THAT IS THE OLDEST RULE ON THIS PAGE. Every other tab in this app navigates;
     * these mark progress, and anything that looks clickable here is an invitation to click backwards
     * into a step whose preconditions have gone.
     *
     * ⭐ A FINISHED STEP IS A TICK, NOT ITS NUMBER. The ordinal answers "which one is this"; once a
     * step is behind them the useful fact is that it is DONE - and four identical numbered pills
     * showing position and never progress was the whole of the old indicator's problem.
     */
    private static Mark = (Ord: number, At: number, Word: string, Skipped: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Cls: string = "wstep next";
            let Dot: string = String(Ord);
            let Now: string = "";
            if (Ord < At) { Cls = "wstep done"; Dot = AppIcons.Svg(AppIcons.Check, 16); }
            if (Ord < At && Skipped === true) { Cls = "wstep done skipped"; Dot = "&#8211;"; }
            if (Ord === At) { Cls = "wstep now"; Now = " aria-current=\"step\""; }
            return "<li class=\"" + Cls + "\"" + Now + "><span class=\"wdot\">" + Dot
                + "</span><span class=\"wname\">" + kHTMLCore.EscapeHtml(Word) + "</span></li>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Mark");
            return "";
        }
    };

    /**
     * The standing promise, under the card rather than in it.
     *
     * ⚠ IT IS THE SAME ON EVERY STEP, which is why it sits outside: inside the card it would compete
     * with the one instruction the visitor is meant to follow next, on all four screens.
     *
     * ⛔ BOTH HALVES ARE LITERALLY TRUE AND MUST STAY THAT WAY. The archive lives in storage the
     * subscriber owns (owner ruling 2026-09-23), and the 64-digit key is used in this page and never
     * sent anywhere. A broader claim - "we never keep a copy" - would be false: the service holds a
     * decrypted archive, a journal and a built folder in order to do the work at all.
     */
    private static Vouch = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ OWNER 2026-11-01: "no marketing here" - the standing promise is no longer drawn.
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Vouch");
            return "";
        }
    };

    /**
     * A step's body with its refusal - once.
     *
     * ⭐ THE REFUSAL SITS DIRECTLY UNDER THE CONTROL THAT PRODUCED IT (2026-10-01). Each step places
     * `Note` itself, under its field; it used to be appended after the whole body, which put it below
     * the small print and glued to it. ⚠ A step that did not place one still gets it at the end, so no
     * refusal can ever go unshown.
     */
    private static WithNote = (Body: string, State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Body.indexOf("id=\"" + AppSignUp.WhyId + "\"") >= 0) { return Body; }
            return Body + AppSignUp.Note(State);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.WithNote");
            return "";
        }
    };

    private static Body = (Step: string, State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Step === AppSignUp.Number) { return AppSignUp.AskNumber(State); }
            if (Step === AppSignUp.Connect) { return AppSignUp.AskConnect(State); }
            if (Step === AppSignUp.Dropbox) { return AppSignUp.AskDropbox(State); }
            if (Step === AppSignUp.Import) { return AppSignUp.AskImport(State); }
            return AppSignUp.AllDone(State);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Body");
            return "";
        }
    };

    // ── 1. The number ────────────────────────────────────────────────────────────────────────────

    /**
     * ⚠ THE NUMBER IS `type="tel"` AND CARRIES AN EXAMPLE. A heavy WhatsApp user knows their number
     * without the country code, and the country code is the whole of the key this product files
     * them under - so the example is doing real work, not decorating.
     *
     * ⚠ IT SAYS WHAT HAPPENS NEXT. The claim is worth nothing on its own; the very next screen
     * needs the handset in their hand within about a minute. Somebody who starts this on a laptop
     * with the phone upstairs should find that out here, not at the code.
     */
    private static AskNumber = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppSignUp.Head("Which WhatsApp number is this for?")
                // ⚠ A REAL LABEL (2026-10-01). It was a bold paragraph, which a screen reader does not
                // tie to the box and a tap does not focus.
                // ⭐ Screen-reader only (owner review 2026-11-02: "Remove the subtitle") - the heading already asks it.
                + "<label class=\"sr\" for=\"wac-number\">Your WhatsApp number</label>"
                + "<div class=\"fieldrow\"><input id=\"wac-number\" type=\"tel\" inputmode=\"tel\""
                + " autocomplete=\"tel\""
                + AppSignUp.Go("wac-claim") + " value=\""
                + kHTMLCore.EscapeAttr(AppSignUp.NumberShown(State)) + "\">"
                + AppSignUp.Button("wac-claim", "Continue", State, "Checking the number") + "</div>"
                + AppSignUp.Note(State)
                // ⭐ The deadline stays visible (owner needs it), in one short line.
                + AppSignUp.Hint("Have that phone to hand - the code works for " + AppSignUp.CodeLifeWords + ".");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.AskNumber");
            return "";
        }
    };

    // ── 2. The phone ─────────────────────────────────────────────────────────────────────────────

    /**
     * ⚠ IT WARNS BEFORE IT ASKS, because the code is the one thing here that cannot be retried
     * freely: a code that is requested and never typed wedges the pairing until it is cleared.
     */
    private static AskConnect = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⚠ LINKED FIRST. `StepFor` moves on from the same fact, so this branch is only reached
            // on the paint between the answer arriving and the step being re-derived - but showing
            // a dead code in that gap would send somebody who has just finished back to their phone
            // to type something that can no longer do anything.
            if (State.Linked === true) { return AppSignUp.AskDropbox(State); }
            if (nz(State.Code).length > 0) { return AppSignUp.ShowCode(State); }
            if (State.Waiting === true) { return AppSignUp.Waiting(State); }
            return AppSignUp.Head("Link " + nz(State.Number) + " to WACommander")
                + "<p class=\"lede\">This is what proves the number is yours. Have the phone in"
                + " your hand - the code has to be typed into WhatsApp &gt; Linked devices, and it"
                + " works for " + AppSignUp.CodeLifeWords + ".</p>"
                // ⛔ NO WARNING NOTICE HERE, AND IT WAS REMOVED FOR TWO REASONS (owner, 2026-09-23:
                // "Avoid this info message - it looks like an error"). An amber panel with a chip
                // beside an untouched button reads as something having ALREADY gone wrong, on the
                // first screen of a step nobody has started yet - so the one visual language the
                // page has for a real refusal was being spent on a hypothetical.
                //
                // ⚠ AND IT HAD STOPPED BEING TRUE. It warned that an unused code "has to be cleared
                // before you can try again", which was the state of things until
                // `IBridgeAuthState.ClearForRepair` shipped: the service now clears an unfinished
                // pairing itself on the next press. Keeping it would teach a subscriber to fear a
                // dead end the product repairs on its own. The consequence that IS still real - a
                // code lasts about a minute - is already in the lede above, where it belongs.
                + "<div class=\"fieldrow one\">"
                + AppSignUp.Button("wac-connect", "Show me a code", State, "Asking WhatsApp") + "</div>"
                + AppSignUp.Note(State);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.AskConnect");
            return "";
        }
    };

    /**
     * ⛔ THE INSTRUCTIONS ARE BESIDE THE CODE, NOT ABOVE IT ON A PREVIOUS SCREEN. The subscriber has
     * about a minute and is holding a phone; making them remember a menu path they read before the
     * code appeared is how the minute gets spent.
     *
     * ⛔ THE COPY BUTTON IS NOT A SECOND WAY TO ASK FOR A CODE, and that is why it may exist on a
     * screen that deliberately offers nothing else to press: it reads what is already on the page,
     * writes nothing anywhere and costs nothing pressed twice. ⚠ It is rendered directly rather than
     * through `Button`, because `Busy` is TRUE the whole time a code is on screen (see
     * `AppMain.OnConnectState`) and a disabled Copy would be a dead control beside a live deadline.
     */
    private static ShowCode = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppSignUp.Head("Type this into your phone")
                + "<div class=\"codeslab\" role=\"group\" aria-label=\"Your pairing code\"><code id=\"wac-code\">"
                + kHTMLCore.EscapeHtml(nz(State.Code)) + "</code>"
                + "<button type=\"button\" class=\"btn secondary\" id=\"wac-copy\">"
                + AppIcons.Svg(AppIcons.Copy, 16) + "Copy</button></div>"
                + "<p class=\"copied\" id=\"wac-copied\" role=\"status\"></p>"
                + "<ol class=\"guide\">"
                + "<li>On the phone: <b>WhatsApp &gt; Settings &gt; Linked devices &gt; Link a"
                + " device</b>.</li>"
                + "<li>Choose <b>Link with phone number instead</b>.</li>"
                + "<li>Type the code above. This page moves on by itself.</li></ol>"
                + AppSignUp.Hint("The code works for " + AppSignUp.CodeLifeWords + ". If it expires, reload"
                    + " this page and ask for another one.");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.ShowCode");
            return "";
        }
    };

    /**
     * ⛔ NO RETRY BUTTON HERE, AND ITS ABSENCE IS THE DESIGN. A second ask produces a second code,
     * and each new code CANCELS the one before it - so a "try again" offered during the wait would
     * destroy the code that was about to appear, and the subscriber would press it again. Doing
     * nothing is the correct action, so this has to make doing nothing feel like progress.
     *
     * ⭐ WHICH IS WHY THE MOTION IS FUNCTIONAL, NOT DECORATION. With nothing to press, movement is
     * the only evidence this screen can offer that the product is still working - and a page that
     * looks stalled is one somebody reloads, which is the act this whole step is written against.
     * The spinner says "working" and the marquee keeps changing faster than once a second.
     */
    private static Waiting = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ THE COUNT TICKS EVERY SECOND AND THE SENTENCE CHANGES ONCE (2026-10-01): `AppMain.OnWaitTick`
            // writes both in place (`WaitedId`, `WaitHintId`) - a repaint would restart the animations.
            // ⚠ `role="status"` on the heading block only: a live region around the ticking count would
            // have a screen reader read a number every second.
            let Sec: number = Math.max(0, Math.floor(Number(State.WaitedSec)));
            if (State.Slow === true && Sec < AppSignUp.SlowAfterSec) { Sec = AppSignUp.SlowAfterSec; }
            return "<div class=\"alive\">"
                + "<span class=\"ring\" aria-hidden=\"true\"></span>"
                + "<div role=\"status\">" + AppSignUp.Head("Asking WhatsApp for a code")
                + "<p class=\"lede\">for " + kHTMLCore.EscapeHtml(nz(State.Number)) + "</p></div>"
                + "<div class=\"marquee\" aria-hidden=\"true\"><span></span></div>"
                + "<p class=\"waited\" id=\"" + AppSignUp.WaitedId + "\" aria-hidden=\"true\">"
                + kHTMLCore.EscapeHtml(AppSignUp.WaitedText(Sec)) + "</p></div>"
                + "<p class=\"hint\" id=\"" + AppSignUp.WaitHintId + "\" role=\"status\">"
                + kHTMLCore.EscapeHtml(AppSignUp.WaitHint(Sec)) + "</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Waiting");
            return "";
        }
    };

    // ── 3. Dropbox ───────────────────────────────────────────────────────────────────────────────

    /**
     * The paste-the-code grant.
     *
     * ⭐ BOTH HALVES ARE ON SCREEN AT ONCE, NUMBERED, AND THAT IS DELIBERATE. Dropbox never sends
     * them back here - there is no redirect URI, which is the whole reason this flow needs no web
     * application - so the page cannot know they have approved. Hiding the box until they press
     * would strand anybody who already has a code, and a page with fewer remembered sub-positions
     * is a page a reload cannot land wrongly in.
     */
    private static AskDropbox = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(State.DropboxUrl).trim().length === 0) { return AppSignUp.NoDropboxUrl(State); }
            // ⭐ ONE PRIMARY AT A TIME (`FinishLeads`): open Dropbox leads until a code is in the box.
            let Finish: string = "secondary";
            if (AppSignUp.FinishLeads(State.DbxTyped) === true || State.Busy === true) { Finish = "primary"; }
            // ⭐ OWNER 2026-11-02: "Each new user should be able to select between either OneDrive or Dropbox" - with
            // OneDrive offered the step is an equal choice of two (`ChoiceHead`), Dropbox's steps then OneDrive's button.
            const Choice: boolean = nz(State.OneDriveUrl).trim().length > 0;
            return AppSignUp.Won(nz(State.Number) + " is linked")
                + AppSignUp.Head(Choice === true ? AppSignUp.ChoiceHead : "Connect your Dropbox")
                + (Choice === true ? "<p class=\"lede\">Keep your archive in the Dropbox or the OneDrive you already"
                    + " have. WACommander gets its own folder there, <b>Apps/WACommander</b>, and cannot see anything else."
                    + "</p><h3 class=\"choice\">Dropbox</h3>" : "")
                + (Choice === true ? "" : "<p class=\"lede\">Use the Dropbox you already have - that is where your archive"
                // ⚠ The folder is named as the done card names it (bold, as Dropbox shows it), not in code font.
                + " will live, in the folder <b>Apps/WACommander</b>. WACommander cannot see anything"
                + " else in your Dropbox.</p>")
                + "<ol class=\"guide\">"
                + "<li>" + AppSignUp.DropboxLink(State, Finish !== "primary") + "</li>"
                + "<li>Approve it, and Dropbox will show you a code. Copy it and come back here."
                + AppSignUp.Label("wac-dbxcode", "The code Dropbox shows you")
                + "<div class=\"fieldrow\"><input id=\"wac-dbxcode\" type=\"text\""
                + " autocomplete=\"off\" autocapitalize=\"none\" autocorrect=\"off\" spellcheck=\"false\""
                + AppSignUp.Go("wac-dbxsave")
                + " value=\"" + kHTMLCore.EscapeAttr(nz(State.DbxTyped)) + "\">"
                + AppSignUp.Button("wac-dbxsave", "Finish connecting", State, "Connecting", Finish) + "</div>"
                + AppSignUp.Note(State) + "</li>"
                + "</ol>"
                + AppSignUp.OneDriveOffer(State);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.AskDropbox");
            return "";
        }
    };

    /**
     * ⭐ WAVE 3: "Connect OneDrive" - ONLY when the service offered it (`OneDriveUrl`). ⚠ THE SAME TAB, unlike the
     * Dropbox link: Microsoft sends the subscriber BACK to this site with the code, so nothing is pasted; the page keeps
     * what it needs in this tab's `sessionStorage` on the click (`AppMain.OnOneDriveGo`). A secondary button: Dropbox
     * stays the step's lead.
     */
    /** The step's heading when both stores are offered. */
    public static readonly ChoiceHead: string = "Choose where your archive lives";

    public static OneDriveOffer = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(State.OneDriveUrl).trim().length === 0) { return ""; }
            return "<h3 class=\"choice\">OneDrive</h3>"
                + AppSignUp.Hint("Sign in with your personal Microsoft account; a work or school OneDrive cannot hold"
                    + " the archive yet.")
                + "<div class=\"fieldrow\"><a class=\"btn secondary\" id=\"wac-onedrive\""
                + " href=\"" + kHTMLCore.EscapeAttr(nz(State.OneDriveUrl)) + "\">Connect OneDrive</a>"
                + "<span class=\"aside\">Microsoft brings you back to this page.</span></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.OneDriveOffer");
            return "";
        }
    };

    /**
     * ⛔ A NEW TAB, AND `rel="noopener"` WITH IT.
     *
     * Same tab would be the ordinary choice and it is wrong here: with no redirect URI Dropbox has
     * nowhere to send them back TO, so leaving this page means finding their way back by Back
     * button with a code in the clipboard and a half-finished sign-up behind them. ⚠ `noopener`
     * because a page opened with `_blank` can otherwise reach `window.opener` and re-point this one.
     */
    private static DropboxLink = (State: SignUpState, Leads: boolean = true): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Kind: string = "primary";
            if (Leads !== true) { Kind = "secondary"; }
            return "<a class=\"btn " + Kind + "\" id=\"wac-dropbox\" target=\"_blank\" rel=\"noopener\""
                + " href=\"" + kHTMLCore.EscapeAttr(nz(State.DropboxUrl)) + "\">"
                + "Open Dropbox and allow WACommander</a>"
                + "<span class=\"aside\">Opens in a new tab - keep this one.</span>"
                + AppSignUp.Frozen();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.DropboxLink");
            return "";
        }
    };

    /**
     * What to do when Dropbox itself says the app is full.
     *
     * ⛔ A FROZEN DROPBOX APP SAYS SO ON DROPBOX'S PAGE AND ISSUES NO CODE, so nothing ever reaches the
     * service to explain it - the answer has to be on this page already. In ink: our end, not theirs.
     * ⭐ FOLDED (2026-10-01): it is the answer to a rare question, and four lines of it sat between
     * step 1 and step 2 for every visitor. A native `<details>` - no script, opens with a tap or Enter.
     */
    public static Frozen = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<details class=\"more\"><summary>Dropbox says this app has reached its user limit?</summary>"
                + "<p class=\"frozen\">" + kHTMLCore.EscapeHtml(AppSignUpApi.FrozenHint()) + "</p></details>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Frozen");
            return "";
        }
    };

    /**
     * ⛔ A MISSING URL IS THE DEPLOYMENT'S FAULT AND MUST NOT READ AS THE VISITOR'S. It means the
     * service has no Dropbox app key configured, so there is nothing they can do and no button that
     * could work. Showing a dead "Connect" here would have them pressing it and blaming themselves.
     */
    // ⭐ TWO THINGS ADDED 2026-10-01. (1) UNTIL THE SERVICE HAS ANSWERED (`DropboxAsked`) the card says it
    // is fetching the page - it used to flash "Dropbox is not available" for the length of that request,
    // on the screen right after the phone linked. (2) "TRY AGAIN": asking for the page changes nothing,
    // so it may be asked again - the card had nothing to press and only a reload could recover it.
    private static NoDropboxUrl = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (State.DropboxAsked !== true) {
                return AppSignUp.Won(nz(State.Number) + " is linked")
                    + AppSignUp.Head("Connect your Dropbox")
                    + "<p class=\"progress\" role=\"status\"><span class=\"spin\" aria-hidden=\"true\"></span>"
                    + "Getting the Dropbox page&#8230;</p>";
            }
            return AppSignUp.Head("Dropbox is not available right now")
                + AppSignUp.Notice("info", "WACommander cannot offer the Dropbox step just now -"
                    + " this is at our end, and your WhatsApp link is safe.")
                + "<div class=\"fieldrow one\">"
                + AppSignUp.Button("wac-dbxretry", "Try again", State, "Asking again") + "</div>"
                + AppSignUp.Hint("Nothing is wrong with your Dropbox account. If it still does not work,"
                    + " come back later - this page will pick up from here.");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.NoDropboxUrl");
            return "";
        }
    };

    // ── 4. The history ───────────────────────────────────────────────────────────────────────────

    /**
     * ⛔ THE ORDER OF THE TWO PHONE INSTRUCTIONS IS LOAD-BEARING.
     *
     * Turning on the 64-digit key comes FIRST, because switching modes generates a NEW key and the
     * next backup is written under it - do it after copying the file and the key will not open the
     * file you already have. ⚠ And password mode cannot be used at all: that key is escrowed with
     * Meta and never reaches the phone, so a subscriber who has a password will get no further
     * without switching. Saying that here costs two lines; discovering it at the key box costs the
     * sign-up.
     *
     * ⚠ THE FILE IS OPENED LOCALLY - NO CLOUD IS INVOLVED BEFORE THIS PAGE HAS READ IT. That is
     * what deleted the Google Picker and the upload-to-Drive step that used to precede it.
     */
    private static AskImport = (State: SignUpState, Later: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(State.PickedName).trim().length > 0 && nz(State.PickedKind).length > 0) {
                return AppSignUp.AskPlain(State, Later);
            }
            if (nz(State.PickedName).trim().length > 0) { return AppSignUp.AskKey(State, Later); }
            let Lede: string = "Last step.";
            let Tail: string = AppSignUp.SkipOffer(State);
            // ⚠ LATER, IT SAYS WHAT A NEW BACKUP DOES TO THE OLD ONE. ⭐ Since 2026-09-24 it can
            // never take a message away: the service keeps every backup that holds something the
            // others do not, whichever is older (`ArchiveSet` in the Bridge). It used to REPLACE the
            // history, and an older snapshot silently lost weeks. No skip: nothing to skip to.
            if (Later === true) {
                Lede = "It is added to your history - nothing is lost.";
                // `backlink` (audit 2026-10-12): a line that is only a link gets a 44px tap target on a phone (app.css).
                Tail = "<p class=\"hint backlink\"><a href=\"#all\">Back to my archive</a></p>";
            }
            return AppSignUp.Head("Bring your history across")
                + "<p class=\"lede\" title=\"The file is unlocked here, in this page - your 64-digit key never"
                + " leaves the device you type it on.\">" + kHTMLCore.EscapeHtml(Lede)
                + "<span class=\"sr\"> The file is unlocked here, in this page - your 64-digit key never leaves the"
                + " device you type it on.</span></p>"
                + AppSignUp.PhoneSteps()
                + AppSignUp.IphoneGuide()
                + AppSignUp.Label("wac-pick", "Your backup file")
                // ⚠ NO `accept`: an iPhone's database has a hashed name and NO extension, so any filter
                // would hide the one file an iPhone user must choose. The page looks inside whatever is
                // chosen instead (`ImportPlain`), and a wrong file gets a sentence.
                + "<div class=\"drop\"><input id=\"wac-pick\" type=\"file\"></div>"
                + AppSignUp.Note(State)
                + Tail;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.AskImport");
            return "";
        }
    };

    /** The ids `AppMain` binds on the no-key import: send it, or choose another file. ⚠ Seams. */
    public static readonly PlainGoId: string = "wac-plaingo";
    public static readonly RepickId: string = "wac-repick";

    /**
     * How an iPhone user gets their history: folded away under the Android steps, because most
     * people on this step need only one of the two. The words are `ImportPlainRules.IphoneSteps`.
     *
     * ⚠ SAME FILE BOX: the page tells an iPhone database from an Android backup by looking inside it.
     */
    private static IphoneGuide = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Steps: string = "";
            for (const One of ImportPlainRules.IphoneSteps) { Steps = Steps + "<li>" + kHTMLCore.EscapeHtml(One) + "</li>"; }
            return "<details class=\"iphone\"><summary>Using an iPhone?</summary>"
                + "<p class=\"hint\">An iPhone has no 64-digit key. Its WhatsApp messages come from a backup of"
                + " the phone on a computer, so do this step on that computer.</p>"
                + "<ol class=\"guide\">" + Steps + "</ol></details>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.IphoneGuide");
            return "";
        }
    };

    /**
     * A chosen database that needs NO KEY (an iPhone's): what it is, one button, the progress line.
     *
     * ⚠ "CHOOSE A DIFFERENT FILE" IS HERE because nothing else on this card leads back to the file
     * box. ⚠ The progress line is the same element the key path uses - `AppMain.OnImportStep` writes
     * the upload's percentage into it.
     */
    private static AskPlain = (State: SignUpState, Later: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Off: string = "";
            if (State.Busy === true) { Off = " disabled"; }
            return AppSignUp.ChosenCard(State, Later, "Bring your history across",
                "<p class=\"lede\">" + kHTMLCore.EscapeHtml(ImportPlainRules.ChosenWords(nz(State.PickedKind))) + "</p>"
                + "<div class=\"fieldrow one\">" + AppSignUp.Button(AppSignUp.PlainGoId, "Import", State, "Importing")
                + "<button type=\"button\" class=\"btn secondary\" id=\"" + AppSignUp.RepickId + "\"" + Off
                + ">Choose a different file</button></div>",
                ImportPlainRules.SendHint);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.AskPlain");
            return "";
        }
    };

    /**
     * The way past this step for somebody who has no backup to bring.
     *
     * ⛔ WITHOUT IT SOMEBODY WITH NOTHING TO IMPORT COULD NOT FINISH SIGNING UP. An Android user who
     * never made an end-to-end key backup, or an iPhone user with no computer backup, has nothing
     * this step can open - so the only thing on screen was an
     * instruction they could not follow, at the last step, after linking their phone and Dropbox.
     * ⚠ It no longer says "Using an iPhone": an iPhone's history CAN be imported (`IphoneGuide`).
     *
     * ⛔ IT SAYS WHAT SKIPPING COSTS, IN THE SAME BREATH AS THE BUTTON. History from before today
     * will not appear; new messages will. A skip that hid that would be found out a week later as
     * "my archive is missing everything", which reads as the product losing their messages.
     *
     * ⚠ A GHOST BUTTON, NOT A PRIMARY ONE: the import is still the better outcome for anybody who
     * can do it, so the page must not make skipping look like the recommended path.
     */
    /**
     * What skipping the import costs - TRUE FOR THE SERVICE THE VISITOR IS ON (2026-10-13). `Kept` = it keeps the
     * messages WhatsApp hands over when a device is linked (`SignUpState.LinkHistory`); then "will not appear" would
     * be false for those, so the sentence says which messages are meant. ⛔ No amounts, no times: what WhatsApp hands
     * over is its own choice and may be nothing. Not kept = the words as they always were.
     * DOCUMENTED EXCEPTION: returns one of two constant sentences. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static SkipCost = (Kept: boolean): string => {
        if (Kept === true) {
            return "WACommander keeps the recent messages WhatsApp hands over when a device is linked, if it hands over any."
                + " Older messages will not appear. Every message you send or receive from now on will.";
        }
        return "Your messages from before today will not appear in WACommander."
            + " Every message you send or receive from now on will.";
    };

    /**
     * The done card's first line for a skipped import - the same rule as `SkipCost`.
     * DOCUMENTED EXCEPTION: returns one of two constant sentences. ERROR-SUPPRESSED-JUSTIFIED: cannot throw.
     */
    public static SkippedFact = (Kept: boolean): string => {
        if (Kept === true) {
            return "<li><b>Your archive starts with what WhatsApp handed over when you linked.</b> That is recent messages,"
                + " if it handed over any; older ones will not appear. It first shows up "
                + AppUpdateEvery.Within + " of the first message it holds.</li>";
        }
        return "<li><b>Your archive starts today.</b> Messages from before today will not"
            + " appear; it first shows up "
            + AppUpdateEvery.Within + " of your next message.</li>";
    };

    private static SkipOffer = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Off: string = "";
            if (State.Busy === true) { Off = " disabled"; }
            return "<div class=\"skip\"><p class=\"skiphead\">No backup to bring?</p>"
                + "<button type=\"button\" class=\"btn secondary\" id=\"wac-skipimport\"" + Off + ">"
                + "Skip - start from today</button>"
                + "<p class=\"hint\">" + AppSignUp.SkipCost(State.LinkHistory === true) + "</p></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.SkipOffer");
            return "";
        }
    };

    /**
     * ⚠ THE PATH IS SPELLED OUT BECAUSE IT IS NOT WHERE ANYBODY WOULD LOOK.
     * `Android/media/com.whatsapp/...` is deliberately readable by other apps, which is why WhatsApp
     * moved the local backup there - but no subscriber will guess it.
     *
     * ⚠ UNVERIFIED ON A REAL HANDSET: whether Android's file chooser will browse INTO that folder
     * from a browser file input. If it will not, the fallback is to copy the file out with the Files
     * app first, and this copy will need a sentence saying so.
     */
    private static PhoneSteps = (): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<ol class=\"guide\">"
                + "<li title=\"A password will not work. Do this first: changing it makes a new key.\">WhatsApp"
                + " &gt; Settings &gt; Chats &gt; Chat backup &gt; End-to-end encrypted backup: use a"
                + " <b>64-digit key</b> and save it.<span class=\"sr\"> A password will not work. Do this first:"
                + " changing it makes a new key.</span></li>"
                + "<li>Tap <b>Back up</b>.</li>"
                + "<li title=\"Any dated copy there is safe; the one with no date is the newest.\">Choose"
                + " <code>Android/media/com.whatsapp/WhatsApp/Databases/msgstore.db.crypt15</code><span class=\"sr\"> Any dated copy there is safe; the"
                + " one with no date is the newest.</span></li>"
                + "</ol>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.PhoneSteps");
            return "";
        }
    };

    /**
     * The key box, shown only once a file is chosen.
     *
     * ⛔ `type="password"` AND `autocomplete="off"`. This key opens every backup that account will
     * ever make and WhatsApp cannot rotate it, so it must not go into a browser's saved-form store
     * or sit legible on a screen in a public place. ⚠ It is never sent anywhere: `WaCrypt15` uses it
     * in this page and nothing else ever sees it, which is the sentence under the box.
     */
    private static AskKey = (State: SignUpState, Later: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppSignUp.ChosenCard(State, Later, "Your 64-digit key",
                "<p class=\"lede\">From WhatsApp &gt; Settings &gt; Chats &gt; Chat backup &gt;"
                + " End-to-end encrypted backup. Spaces do not matter.</p>"
                // ⚠ NO `inputmode`: it said "latin", which is not a value a browser knows; the key is
                // hex (digits and a-f), so the ordinary keyboard is the right one.
                + AppSignUp.Label("wac-key", "The 64-digit key")
                + "<div class=\"fieldrow\"><input id=\"wac-key\" type=\"password\""
                + " autocomplete=\"off\" autocapitalize=\"none\" autocorrect=\"off\" spellcheck=\"false\""
                + AppSignUp.Go("wac-unlock") + ">"
                + AppSignUp.Button("wac-unlock", "Unlock and import", State, "Importing") + "</div>",
                "The key is used in this page and is never sent to WACommander.");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.AskKey");
            return "";
        }
    };

    /**
     * The frame of a card for a CHOSEN file (`AskKey`, `AskPlain`): what was chosen, the heading, the card's own
     * `Body`, the sentence, the progress line, the hint and - only on the sign-up, never on the later import (there is
     * no sign-up to finish without one) - the way to skip. ⚠ The skip is offered here too: somebody who chose a file
     * and then finds they never wrote the key down has exactly the same way out, and no other.
     *
     * ⚠ THE PROGRESS LINE IS AN ELEMENT, NOT A RE-RENDER. This moves tens of megabytes over a phone connection; a quiet
     * minute reads as a hung page and a hung page gets closed, half way through. `AppMain.OnImportStep` writes here
     * directly so the box is not destroyed and rebuilt on every percentage - which is also why it stays a PLAIN TEXT
     * line: that handler sets `textContent`, so any child markup here would be wiped by the first percentage anyway.
     */
    private static ChosenCard = (State: SignUpState, Later: boolean, Heading: string, Body: string, HintText: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Tail: string = AppSignUp.SkipOffer(State);
            if (Later === true) { Tail = ""; }
            return AppSignUp.Won("Chosen: " + nz(State.PickedName))
                + AppSignUp.Head(Heading)
                + Body
                + AppSignUp.Note(State)
                + "<p class=\"progress\" id=\"wac-progress\" role=\"status\"></p>"
                + AppSignUp.Hint(HintText)
                + Tail;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.ChosenCard");
            return "";
        }
    };

    // ── Done ─────────────────────────────────────────────────────────────────────────────────────

    /**
     * How long the first archive takes, in the words the subscriber is given.
     *
     * ⛔ IT IS THE SERVICE'S REFRESH INTERVAL, NOT A GUESS, AND A BRIDGE TEST HOLDS THEM EQUAL.
     * Import, rebuild and the Dropbox push all run in ONE refresh cycle (measured 2026-09-23: the
     * owner's import, folder and 55-file push shared a single tick), so the first archive lands
     * within one interval of the history arriving. ⚠ If `BridgeEnv.DefaultRefreshSeconds` moves and
     * this does not, the page promises a time the service does not keep - and the subscriber's first
     * experience of the product is it being late.
     */
    public static readonly FirstBuildWords: string = AppUpdateEvery.Within;

    /**
     * How long a pairing code works, in the ONE phrase every screen uses (audit 2026-10-12: the number step said
     * "about a minute", the link step "within about a minute" and the code screen "a couple of minutes").
     *
     * ⛔ A DEADLINE, NOT A FORECAST - it is the life the service gives a code (`PairReply.LifeSeconds`, 150 s), and
     * `SignUpPromise.test.ts` holds `CodeLifeSeconds` equal to it, so the words cannot drift from the service.
     */
    public static readonly CodeLifeSeconds: number = 150;
    public static readonly CodeLifeWords: string = "a couple of minutes";

    /**
     * The finished card.
     *
     * ⛔ REWRITTEN 2026-09-24 BECAUSE THE OWNER FOUND IT CONFUSING, and it was, four ways:
     *   * "your archive is building IN peter@kalmstrom.com" - an email address is not a place; it
     *     read as if the archive were going into their EMAIL. It is the Dropbox ACCOUNT's name.
     *   * "ALL DONE" above "is building" - finished and not finished in one breath, with no word on
     *     how long. The eyebrow now says "Signed up" and the card says when the archive arrives.
     *   * "is connected" - to what? It is WhatsApp, linked to WACommander.
     *   * `Apps/WACommander` in code font - a developer's path. Dropbox's own UI shows it as a
     *     folder called WACommander inside Apps, which is what this now says.
     * ⭐ SO IT IS THREE PLAIN FACTS: what is happening now, where it will be, what happens next.
     */
    private static AllDone = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<div class=\"finished\">"
                + "<span class=\"tick\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Check, 16) + "</span>"
                + AppSignUp.Head("You're set up") + "</div>"
                + "<p class=\"lede\">WhatsApp on <b>" + kHTMLCore.EscapeHtml(nz(State.Number))
                + "</b> is linked to WACommander.</p>"
                + AppSignUp.Facts(State)
                // ⭐ What became of the uploaded backup, in place ("" when there is nothing to show). ⛔ Above
                // the button and never instead of it: "Open WACommander" works whatever the import is doing.
                + AppImportView.Html(State.Upload)
                + "<div class=\"fieldrow one\">" + AppSignUp.OpenButton("Open WACommander") + "</div>"
                + AppSignUp.Hint(AppSignUp.LeaveWords);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.AllDone");
            return "";
        }
    };

    /** The id `AppMain` binds on the "open the archive" link. ⚠ A seam - see `OpenButton`. */
    public static readonly OpenId: string = "wac-open";

    /**
     * The link that opens the archive from a finished card.
     *
     * ⛔ IT CARRIES AN ID BECAUSE `href="#all"` ALONE WAS A DEAD CLICK WHEN THE ADDRESS WAS ALREADY
     * `#all` (2026-09-24). A card painted by the resumed Dropbox step or by "Import a backup" can sit
     * on that route, and following a link to the fragment you are already on fires no `hashchange` -
     * so nothing happened. `AppMain` binds the id and opens the archive itself.
     */
    public static OpenButton = (Word: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<a class=\"btn primary\" id=\"" + AppSignUp.OpenId + "\" href=\"#all\">"
                + kHTMLCore.EscapeHtml(Word) + "</a>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.OpenButton");
            return "";
        }
    };

    /**
     * "Import a backup" - the history step, AFTER sign-up (2026-09-24).
     *
     * ⭐ IT REUSES THE SIGN-UP'S OWN IMPORT STEP - the phone instructions, the file chooser, the key
     * box that never leaves the page, the progress line - so there is one way to bring a history and
     * it looks the same whenever it is done. ⚠ Without the trail (this is not step 4 of anything) and
     * without "Skip" (there is nothing to skip to).
     * ⛔ IT SAYS WHEN THE RESULT SHOWS UP. The upload only puts the file in their Dropbox; the service
     * imports it on its next cycle, so "done" without a time reads as "it did nothing".
     */
    public static LaterHtml = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ AN EARLIER UPLOAD THIS BROWSER REMEMBERS (a reload, a return to this page) is said ABOVE the
            // step, so a refusal is read before the same file is chosen again.
            let Body: string = AppSignUp.EarlierUpload(State) + AppSignUp.AskImport(State, true);
            if (State.Imported === true) { Body = AppSignUp.LaterDone(State); }
            return "<section class=\"signup\"><div class=\"panel\">"
                + "<header class=\"crown\"><p class=\"eyebrow\">Import a backup</p></header>"
                + AppSignUp.WithNote(Body, State) + "</div>" + AppSignUp.Vouch() + "</section>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.LaterHtml");
            return "";
        }
    };

    /** The words the later import ends on. ⚠ The time is the service's refresh interval. */
    public static LaterDoneWords = (): string => {
        // DOCUMENTED EXCEPTION: composes a constant sentence; nothing here can throw.
        return "Your backup is in your Dropbox. WACommander imports it on its next pass, so your"
            + " history appears in your archive " + AppUpdateEvery.Within
            + ". Every message WACommander has received since you signed up stays in it.";
    };

    /**
     * The uploaded card. ⭐ WITH A STATUS (`State.Upload`) IT SAYS WHAT IS HAPPENING TO THE BACKUP, in place,
     * instead of the forecast - and the refusal, when the service refuses it. Without one (an older service,
     * an upload this page cannot ask about) it says what it always said.
     */
    private static LaterDone = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Middle: string = "<p class=\"lede\">" + kHTMLCore.EscapeHtml(AppSignUp.LaterDoneWords()) + "</p>";
            const Status: string = AppImportView.Html(State.Upload);
            if (Status.length > 0) {
                Middle = "<p class=\"lede\">" + kHTMLCore.EscapeHtml(AppSignUp.LaterKeptWords) + "</p>" + Status;
            }
            return "<div class=\"finished\"><span class=\"tick\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Check, 16) + "</span>"
                + AppSignUp.Head("Backup uploaded") + "</div>" + Middle
                + "<div class=\"fieldrow one\">" + AppSignUp.OpenButton("Back to my archive") + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.LaterDone");
            return "";
        }
    };

    /** What the uploaded card says beside a live status - the one promise that needs no time. */
    public static readonly LaterKeptWords: string = "Every message WACommander has received since you signed up"
        + " stays in your archive, whatever happens to this backup.";

    /** An earlier upload's status above the import step, or "" when there is none to show. */
    private static EarlierUpload = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Status: string = AppImportView.Html(State.Upload);
            if (Status.length === 0) { return ""; }
            return "<div class=\"implast\"><h2 class=\"implasthead\">" + kHTMLCore.EscapeHtml(AppImportView.EarlierHead)
                + "</h2>" + Status + "</div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.EarlierUpload");
            return "";
        }
    };

    /**
     * How to stop, said accurately.
     *
     * ⛔ IT USED TO SAY "DISCONNECT WACOMMANDER UNDER CONNECTED APPS IN DROPBOX WHENEVER YOU LIKE",
     * AND THAT WAS MISLEADING IN THE WAY THAT MATTERS. Removing the app in Dropbox only stops the
     * archive being WRITTEN - the WhatsApp link is a separate linked device and stays live, so the
     * service goes on receiving their messages with nowhere to put them. Somebody who wanted
     * WACommander gone would believe it was gone. The WhatsApp side is the one that ends it.
     */
    // ⭐ AND NOW THERE IS ONE PLACE THAT ENDS ALL OF IT (2026-09-24): "Close my account" at the foot
    // of the page unlinks WhatsApp, withdraws the Dropbox permission and deletes what the service
    // holds. The Dropbox-only route is still named, because it is still the misleading one.
    public static readonly LeaveWords: string = "The folder is yours and stays in your Dropbox."
        + " To stop WACommander, use Close my account at the foot of the page - it removes"
        + " WACommander from your phone's Linked devices, withdraws the Dropbox permission and"
        + " deletes what our service holds. Removing it only under Connected apps in Dropbox stops"
        + " the archive being updated, but WhatsApp stays linked.";

    /**
     * The finished card's three facts - now, where, next.
     *
     * ⚠ THE CLASS IS `facts`, NOT `next`, AND THE RENAME FIXED A VISIBLE BUG. The trail marks its
     * unreached steps `wstep next`, and the stylesheet's `.signup .next` - meant for this list -
     * matched them too, painting every future step in the trail as a grey tinted box at 390px.
     *
     * ⛔ A SKIPPED IMPORT IS NOT PROMISED A HISTORY. "Your archive is being built now" over an
     * empty past would be found out a week later as missing messages; it says what it will hold.
     */
    private static Facts = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Now: string = "<li><b>Your archive is being built now.</b> The first one takes "
                + AppUpdateEvery.UpTo + ".</li>";
            if (State.SkippedImport === true && State.Imported !== true) {
                Now = AppSignUp.SkippedFact(State.LinkHistory === true);
            }
            return "<ul class=\"facts\">" + Now
                + "<li>It is saved " + AppSignUp.WhereSaved(State)
                + ", in the folder <b>Apps/WACommander</b>.</li>"
                + "<li>New messages are added by themselves from now on.</li></ul>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Facts");
            return "";
        }
    };

    /**
     * "in your own Dropbox (peter@...)", or just "in your own Dropbox".
     *
     * ⚠ `AppMain.AccountIn` FALLS BACK TO THE WORDS "your Dropbox" when the service names no
     * account, so repeating the name blindly would print "in your own Dropbox (your Dropbox)".
     */
    private static WhereSaved = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return "in your own Dropbox"; }
        try {
            if (State.Storage === "onedrive") { return "in your own OneDrive"; }
            const Who: string = nz(State.Dropbox).trim();
            if (Who.length === 0 || Who.toLowerCase() === "your dropbox") {
                return "in your own Dropbox";
            }
            return "in your own Dropbox (<b>" + kHTMLCore.EscapeHtml(Who) + "</b>)";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.WhereSaved");
            return "in your own Dropbox";
        }
    };

    /**
     * ⛔ A BUSY BUTTON IS DISABLED AND SAYS SO. Every button on this route starts something that
     * must not happen twice - a second claim, a second pairing code, a second grant - and the gap
     * between a press and an answer is a network round trip somebody will fill with another press.
     */
    // ⭐ THE BUSY WORD SAYS WHAT IS HAPPENING (2026-10-01): "Checking the number…", "Connecting…",
    // "Importing…" - one "Working..." for every button told a visitor nothing about which wait this is.
    // ⚠ `type="button"` on every one: none of them submits anything, and an untyped button is a submit
    // button the day somebody wraps a step in a form.
    private static Button = (Id: string, Word: string, State: SignUpState, BusyWord: string = "Working",
                             Kind: string = "primary"): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Open: string = AppButton.Open(Id, Kind);
            if (State.Busy === true) {
                return Open + " disabled><span class=\"spin\" aria-hidden=\"true\"></span>"
                    + kHTMLCore.EscapeHtml(BusyWord) + "&#8230;</button>";
            }
            return Open + ">" + kHTMLCore.EscapeHtml(Word) + "</button>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Button");
            return "";
        }
    };

    /** The id of the step's heading - where the focus goes when a step changes on a phone. */
    public static readonly HeadId: string = "wac-step";
    /** The id of the refusal under a step's control (`Note`). */
    public static readonly WhyId: string = "wac-why";
    /** The attribute that names the button Enter presses from a field (`AppMain.OnFieldKey`). */
    public static readonly GoAttr: string = "data-wac-go";

    /**
     * A step's heading.
     *
     * ⚠ `tabindex="-1"`: it can be given the focus by the page (never by Tab), so that after a step
     * changes a screen reader starts reading at the new step and the next Tab lands on its first
     * control - instead of at the top of the document, where a repaint used to leave it.
     */
    private static Head = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ THE CARD'S HEADING IS THE PAGE'S FIRST HEADING, so it is an `<h1>` (audit 2026-10-01: every account screen
            // started at `<h2>` and no page had an `<h1>` at all - a screen reader's "go to the main heading" found nothing).
            return "<h1 id=\"" + AppSignUp.HeadId + "\" tabindex=\"-1\">" + kHTMLCore.EscapeHtml(Text) + "</h1>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Head");
            return "";
        }
    };

    /** A field's visible label, tied to it by `for`. */
    private static Label = (For: string, Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<label class=\"flabel\" for=\"" + kHTMLCore.EscapeAttr(For) + "\">"
                + kHTMLCore.EscapeHtml(Text) + "</label>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Label");
            return "";
        }
    };

    /** The attribute that makes Enter in a field press this step's button. */
    /** The Enter-presses-this-button attribute, answered under the caller's own label. */
    public static GoAttrFor = (ButtonId: string, Label: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return " " + AppSignUp.GoAttr + "=\"" + kHTMLCore.EscapeAttr(ButtonId) + "\"";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, Label);
            return "";
        }
    };

    private static Go = (ButtonId: string): string => {
        return AppSignUp.GoAttrFor(ButtonId, "AppSignUp.Go");
    };

    /**
     * Which button Enter presses, from a key event's facts - "" for none.
     *
     * ⚠ ONLY A PLAIN Enter IN A FIELD THAT NAMES A BUTTON. Not while an input method is composing
     * (Enter then chooses a candidate), and not with a modifier held. ⛔ It only ever names a button
     * that is already on the step - the caller clicks it, so `Busy`, `disabled` and every guard in the
     * button's own handler apply exactly as for a press. Nothing here can send a message.
     */
    // ⛔ AND NOT A HELD KEY (`Repeat`, found by the verifier 2026-10-01): the keyboard repeats Enter ~30 times a
    // second, the button is enabled again the moment an answer lands, and the next repeat pressed it again -
    // a refused number was claimed over and over for as long as the key was down, and a refused Dropbox code's
    // sentence was replaced at once by "Paste the code ... first" (the box is emptied with the answer).
    public static EnterTarget = (Key: string, GoId: string, Composing: boolean, Modified: boolean,
                                 Repeat: boolean = false): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Key !== "Enter" || Composing === true || Modified === true || Repeat === true) { return ""; }
            return nz(GoId).trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.EnterTarget");
            return "";
        }
    };

    /** What the number box shows: what was typed, else the number the service filed. */
    private static NumberShown = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (nz(State.Typed).length > 0) { return nz(State.Typed); }
            return nz(State.Number);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.NumberShown");
            return "";
        }
    };

    /** The sentence for a press with nothing in the number box. ⚠ Validation in the page: there is nothing to send. */
    public static readonly NoNumberWhy: string = "Type your WhatsApp number first, with the country code -"
        + " like +46 70 123 45 67.";
    /** The sentence for "Finish connecting" with nothing pasted. */
    public static readonly NoCodeWhy: string = "Paste the code Dropbox showed you first. If you have not"
        + " opened Dropbox yet, start with step 1.";

    /**
     * Which screen of the sign-up is showing - finer than the step, because one step has several
     * (asking, waiting, the code). ⚠ DERIVED, like the step: the page compares it with the last
     * painted one to know whether the focus should move.
     */
    public static ScreenOf = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return AppSignUp.Number; }
        try {
            const Step: string = AppSignUp.StepFor(State);
            if (Step === AppSignUp.Connect && nz(State.Code).length > 0) { return "code"; }
            if (Step === AppSignUp.Connect && State.Waiting === true) { return "waiting"; }
            if (Step === AppSignUp.Import && nz(State.PickedName).trim().length > 0) { return "key"; }
            return Step;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.ScreenOf");
            return AppSignUp.Number;
        }
    };

    /**
     * The element to focus when a NEW screen has just been painted.
     *
     * ⭐ WITH A KEYBOARD (`Typing` - a fine pointer) the screen's own control: the number box, "Show me
     * a code", the Dropbox link, the key box. ⛔ ON A TOUCH SCREEN THE HEADING, never a field: focusing
     * a field there opens the on-screen keyboard over the instructions the visitor has not read yet.
     */
    public static FocusFor = (Screen: string, Typing: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Typing !== true) { return AppSignUp.HeadId; }
            if (Screen === AppSignUp.Number) { return "wac-number"; }
            if (Screen === AppSignUp.Connect) { return "wac-connect"; }
            if (Screen === "code") { return "wac-copy"; }
            if (Screen === AppSignUp.Dropbox) { return "wac-dropbox"; }
            if (Screen === "key") { return "wac-key"; }
            if (Screen === AppSignUp.Done) { return AppSignUp.OpenId; }
            return AppSignUp.HeadId;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.FocusFor");
            return "";
        }
    };

    /**
     * Where the focus goes after the card was painted - "" to leave it alone.
     *
     * ⛔ NEVER TAKEN FROM A CONTROL OUTSIDE THE CARD (`Elsewhere`): an answer that arrives late - the code, the
     * Dropbox page, "linked" - repaints the card while the visitor may be in the theme menu or the footer, and
     * moving the focus would close what they have open and put the keyboard somewhere they did not go.
     * A NEW screen gets its own control (`FocusFor`); the SAME screen painted again keeps the control that had it.
     */
    public static FocusAfter = (Screen: string, Typing: boolean, NewScreen: boolean, Had: string,
                                Elsewhere: boolean): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Elsewhere === true) { return ""; }
            if (NewScreen === true) { return AppSignUp.FocusFor(Screen, Typing); }
            return nz(Had);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.FocusAfter");
            return "";
        }
    };

    /**
     * Is this `/dropbox` answer a finished grant?
     *
     * ⛔ `ok` WITH AN ADDRESS IS THE ANSWER TO "WHERE IS THE DROPBOX PAGE", NEVER A GRANT. The same endpoint
     * answers both (`{ok, url}` to a GET, `{ok, account}` to a code), and reading the first as the second is
     * how "Finish connecting" with an empty box once moved on to the history step with no Dropbox connected.
     * The page's two guards stop that request being sent; this stops its answer being believed.
     */
    public static IsGrant = (Ok: boolean, Url: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Ok === true && nz(Url).trim().length === 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.IsGrant");
            return false;
        }
    };

    /** The waiting screen's ticking count and its sentence - both written in place by `AppMain.OnWaitTick`. */
    public static readonly WaitedId: string = "wac-waited";
    public static readonly WaitHintId: string = "wac-waithint";

    /** After this many seconds of waiting for a code the hint changes to "Still waiting". */
    public static readonly SlowAfterSec: number = 30;

    /** "12 s" / "1 min 05 s" - the waiting screen's ticking count. */
    public static WaitedText = (Seconds: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const S: number = Math.max(0, Math.floor(Number(Seconds)));
            if (isNaN(S) === true) { return ""; }
            return AppImportView.Elapsed(S);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.WaitedText");
            return "";
        }
    };

    /**
     * The sentence under the waiting bar, for a wait that has gone on `Seconds`.
     *
     * ⭐ IT CHANGES ONCE, AT `SlowAfterSec`, AND THE PAGE WRITES IT IN PLACE. Until 2026-10-01 the
     * "Still waiting" sentence could never appear: the flag was set and nothing painted it.
     */
    public static WaitHint = (Seconds: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Number(Seconds) < AppSignUp.SlowAfterSec) {
                // ⚠ No forecast (the style guide: show progress, never promise a wait) - the count above ticks.
                return "Keep this page open and have the phone ready - the code appears here by itself.";
            }
            return "Still waiting. This is normal when the service has just started. Keep this page open"
                + " rather than reloading, because asking again would cancel the code on its way.";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.WaitHint");
            return "";
        }
    };

    /**
     * On the Dropbox step, is "Finish connecting" the main action yet?
     *
     * ⭐ ONE PRIMARY BUTTON AT A TIME. The step has two things to press, in order: open Dropbox, then
     * finish. With nothing pasted the main action is opening Dropbox; once a code is in the box it is
     * finishing. Two filled buttons at once said neither.
     */
    public static FinishLeads = (Pasted: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return nz(Pasted).trim().length > 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.FinishLeads");
            return false;
        }
    };

    /**
     * A quiet line under a step - the small print, and never a refusal.
     *
     * ⚠ `Notice` IS WHAT A REFUSAL LOOKS LIKE, and the two are deliberately not interchangeable: a
     * sentence the visitor must ACT on cannot be set in the same grey as one they may skip.
     */
    private static Hint = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<p class=\"hint\">" + kHTMLCore.EscapeHtml(Text) + "</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Hint");
            return "";
        }
    };

    /**
     * A step that is behind them, said out loud.
     *
     * ⭐ A WIZARD THAT ONLY EVER ASKS THE NEXT QUESTION NEVER CONFIRMS THE LAST ONE. The trail's
     * tick says a step is done in the abstract; this says WHAT was done - the number that linked,
     * the file that was chosen - which is the half the visitor can actually check.
     */
    private static Won = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<p class=\"won\"><span class=\"tick\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Check, 16) + "</span>"
                + kHTMLCore.EscapeHtml(Text) + "</p>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Won");
            return "";
        }
    };

    /**
     * An inline notice: `"warn"` for something to be careful of, `"bad"` for a refusal.
     *
     * ⛔ IT IS A DESIGNED ELEMENT PRECISELY BECAUSE IT IS NOT AN ERROR. Constitution law 1: a
     * user-facing surface shows success, progress or input VALIDATION, and everything `ClaimGate`
     * refuses is validation - a number already connected, a number that is not one, a service that
     * is full. Routing any of it to `KGlobalErrorHandler` would flip the gate and blank the page the
     * visitor is trying to use, over an answer that is the product working exactly as designed. An
     * orange sentence in body text read like something had gone wrong; this reads like an answer.
     */
    private static Notice = (Kind: string, Text: string, Id: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ `"info"` (2026-10-01): something at OUR end, in ink on the soft ground. Red is for an
            // answer about what the visitor typed; "this is at our end" in red read as their mistake.
            let Cls: string = "notice warn";
            if (Kind === "bad") { Cls = "notice bad"; }
            if (Kind === "info") { Cls = "notice info"; }
            let Named: string = "";
            if (nz(Id).length > 0) { Named = " id=\"" + kHTMLCore.EscapeAttr(Id) + "\""; }
            return "<div class=\"" + Cls + "\"" + Named + " role=\"status\">"
                + "<span class=\"nicon\" aria-hidden=\"true\">" + AppIcons.Svg(AppIcons.Warning, 16) + "</span>"
                + "<span class=\"ntext\">" + kHTMLCore.EscapeHtml(Text) + "</span></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Notice");
            return "";
        }
    };

    /**
     * The refusal under the step.
     *
     * ⛔ A `Notice`, NOT THE ERROR HANDLER - see `Notice` for the law and the reason. ⚠ It renders
     * AFTER the body rather than above it, so the answer sits next to the control that produced it
     * and the step's own instruction stays where the visitor last read it.
     */
    public static Note = (State: SignUpState): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Why: string = nz(State.Why).trim();
            if (Why.length === 0) { return ""; }
            // ⚠ THE SERVICE'S SENTENCES ARE WRITTEN TO FOLLOW A PREFIX ("that number is already
            // connected..."), so on their own they started lower-case under the notice's "!" and
            // read as a fragment. Capitalised here, once, for every refusal the page shows.
            // ⚠ "AT OUR END" IS NOT A REFUSAL OF WHAT THEY TYPED, so it is not red (`Notice`'s "info").
            let Kind: string = "bad";
            if (Why === AppSignUpApi.OursWhy() || Why === AppSignUpApi.NoServiceWhy()) { Kind = "info"; }
            // ⭐ AND NEITHER IS A CONDITION ON THEIR ACCOUNT (WhatsApp's passkey demand): the service FLAGS it
            // (`SignUpAnswer.Passkey` -> `InfoWhy`) - never matched by its words. No flag = the refusal look.
            if (nz(State.InfoWhy).trim().length > 0 && Why === nz(State.InfoWhy).trim()) { Kind = "info"; }
            return AppSignUp.Notice(Kind, Why.charAt(0).toUpperCase() + Why.substring(1), AppSignUp.WhyId);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSignUp.Note");
            return "";
        }
    };
}
